import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Ray } from '@babylonjs/core/Culling/ray';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import type { Texture } from '@babylonjs/core/Materials/Textures/texture';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Scene } from '@babylonjs/core/scene';
import { GrenadeType, ProjectileKind, type SnapshotProjectile } from '@fisura/protocol';

type FxKind = 'boom' | 'flash' | 'canister';

interface Fx {
  mesh: Mesh;
  kind: FxKind;
  bornMs: number;
  lifeMs: number;
  r0: number;
  r1: number;
}

const BOOM_LIFE_MS = 420;
const BOOM_RADIUS = 5;
const FLASH_LIFE_MS = 220;
const FLASH_RADIUS = 6;

// Smoke is a particle system, not animated meshes — puffs spawn continuously
// from the canister, drift up and out, and fade over their own lifetime so new
// ones replace old ones for a living, billowing cloud.
const SMOKE_EMIT_S = 11; // how long the canister keeps puffing
const SMOKE_MAX_LIFE = 9.5; // longest a single puff lives (s)
const SMOKE_CAPACITY = 1000; // max live particles per cloud (CPU system)
// The physical canister stays visible on the ground for the whole cloud (emit
// window + the tail of the last puffs), then fades out with the smoke.
const SMOKE_CANISTER_MS = (SMOKE_EMIT_S + SMOKE_MAX_LIFE) * 1000;

/** Renders server-simulated projectiles (grenades/rockets) and explosion VFX. */
export class ProjectileFx {
  private readonly pool = new Map<number, Mesh>();
  private readonly fx: Fx[] = [];
  private readonly fragMat: StandardMaterial;
  private readonly smokeProjMat: StandardMaterial;
  private readonly flashProjMat: StandardMaterial;
  private readonly rocketMat: StandardMaterial;
  private readonly boomMat: StandardMaterial;
  private readonly flashMat: StandardMaterial;
  private puffTex: Texture | null = null; // soft round particle sprite (lazy)
  private smokeSeq = 0;
  private windX = 0; // current wind vector (world units/s), set per match
  private windZ = 0;

  constructor(private readonly scene: Scene) {
    this.fragMat = new StandardMaterial('proj-frag', scene);
    this.fragMat.diffuseColor = new Color3(0.15, 0.2, 0.12);
    this.smokeProjMat = new StandardMaterial('proj-smoke', scene);
    this.smokeProjMat.diffuseColor = new Color3(0.5, 0.51, 0.55);
    this.flashProjMat = new StandardMaterial('proj-flash', scene);
    this.flashProjMat.diffuseColor = new Color3(0.72, 0.73, 0.78);
    this.flashProjMat.emissiveColor = new Color3(0.25, 0.25, 0.28);
    this.rocketMat = new StandardMaterial('proj-rocket', scene);
    this.rocketMat.disableLighting = true;
    this.rocketMat.emissiveColor = new Color3(1, 0.6, 0.25);

    this.boomMat = new StandardMaterial('proj-boom', scene);
    this.boomMat.disableLighting = true;
    this.boomMat.emissiveColor = new Color3(1, 0.55, 0.2);
    this.boomMat.alpha = 0.85;
    this.flashMat = new StandardMaterial('proj-flashfx', scene);
    this.flashMat.disableLighting = true;
    this.flashMat.emissiveColor = new Color3(1, 1, 0.96);
  }

  /** Update the wind that drifts smoke (world yaw it blows toward + speed). */
  setWind(dir: number, speed: number): void {
    this.windX = Math.sin(dir) * speed;
    this.windZ = Math.cos(dir) * speed;
  }

  private projMat(p: SnapshotProjectile): StandardMaterial {
    if (p.kind === ProjectileKind.Rocket) return this.rocketMat;
    if (p.gtype === GrenadeType.Smoke) return this.smokeProjMat;
    if (p.gtype === GrenadeType.Flashbang) return this.flashProjMat;
    return this.fragMat;
  }

  /** A soft radial-gradient sprite (white → transparent) shared by all clouds. */
  private smokeSprite(): Texture {
    if (this.puffTex) return this.puffTex;
    const size = 64;
    const t = new DynamicTexture('smoke-puff', { width: size, height: size }, this.scene, true);
    const ctx = t.getContext() as unknown as CanvasRenderingContext2D;
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.45, 'rgba(255,255,255,0.55)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    t.hasAlpha = true;
    t.update();
    this.puffTex = t;
    return t;
  }

  /** Sync the live projectile meshes to the interpolated snapshot list. */
  update(projectiles: SnapshotProjectile[]): void {
    const seen = new Set<number>();
    for (const p of projectiles) {
      seen.add(p.id);
      let m = this.pool.get(p.id);
      if (!m) {
        m =
          p.kind === ProjectileKind.Rocket
            ? MeshBuilder.CreateCylinder(`proj-${p.id}`, { height: 0.45, diameter: 0.14, tessellation: 8 }, this.scene)
            : MeshBuilder.CreateSphere(`proj-${p.id}`, { diameter: 0.24, segments: 6 }, this.scene);
        m.material = this.projMat(p);
        m.isPickable = false;
        if (p.kind === ProjectileKind.Rocket) m.rotation.x = Math.PI / 2;
        this.pool.set(p.id, m);
      }
      m.position.set(p.x, p.y, p.z);
    }
    for (const [id, m] of this.pool) {
      if (!seen.has(id)) {
        m.dispose();
        this.pool.delete(id);
      }
    }

    // Animate the brief mesh effects (boom + flash). Smoke runs itself via its
    // ParticleSystem (auto-disposes when it finishes).
    const now = performance.now();
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i]!;
      const t = (now - f.bornMs) / f.lifeMs;
      if (t >= 1) {
        f.mesh.dispose();
        this.fx.splice(i, 1);
        continue;
      }
      f.mesh.scaling.setAll(f.r0 + (f.r1 - f.r0) * this.grow(f.kind, t));
      f.mesh.visibility = this.fade(f.kind, t);
    }
  }

  /** Radius envelope per effect kind, normalized 0..1. */
  private grow(kind: FxKind, t: number): number {
    if (kind === 'flash') return Math.min(1, t / 0.15); // snaps to full almost instantly
    if (kind === 'canister') return 1; // a fixed-size object, no scaling
    return t; // boom expands linearly
  }

  /** Opacity envelope per effect kind, 0..1. */
  private fade(kind: FxKind, t: number): number {
    if (kind === 'flash') return t < 0.35 ? 1 : 1 - (t - 0.35) / 0.65;
    if (kind === 'canister') return t < 0.9 ? 1 : 1 - (t - 0.9) / 0.1; // solid, fades at the very end
    return 1 - t; // boom fades out
  }

  /** Frag/rocket blast — expanding orange sphere + (caller adds shake/audio). */
  private boom(x: number, y: number, z: number): void {
    const m = MeshBuilder.CreateSphere('boom', { diameter: 1, segments: 8 }, this.scene);
    m.material = this.boomMat;
    m.position.set(x, y, z);
    m.isPickable = false;
    this.fx.push({ mesh: m, kind: 'boom', bornMs: performance.now(), lifeMs: BOOM_LIFE_MS, r0: 0.4, r1: BOOM_RADIUS });
  }

  /** Flashbang pop — a brief blinding white sphere. */
  private flash(x: number, y: number, z: number): void {
    const m = MeshBuilder.CreateSphere('flashfx', { diameter: 1, segments: 8 }, this.scene);
    m.material = this.flashMat;
    m.position.set(x, y, z);
    m.isPickable = false;
    this.fx.push({ mesh: m, kind: 'flash', bornMs: performance.now(), lifeMs: FLASH_LIFE_MS, r0: 0.6, r1: FLASH_RADIUS });
  }

  /**
   * Smoke cloud — a CPU particle system anchored at the canister. Puffs emit
   * continuously, drift up and outward, grow, and fade over their own lifetime
   * (so the cloud is always being renewed), then the emitter stops and the last
   * puffs dissipate. Auto-disposes when fully spent.
   */
  /**
   * Floor height just below a point (downward ray against the pickable world).
   * Used to seat smoke on the actual surface — the grenade's rest position sits
   * a radius above the floor (and can settle in a dip or slightly embedded), so
   * placing the cloud/canister there sinks them or buries the cloud underground.
   */
  private floorBelow(x: number, y: number, z: number): number {
    const ray = new Ray(new Vector3(x, y + 0.5, z), new Vector3(0, -1, 0), 300);
    const hit = this.scene.pickWithRay(ray, (m) => m.isPickable && m.isEnabled());
    return hit?.hit && hit.pickedPoint ? hit.pickedPoint.y : y;
  }

  private smoke(x: number, y: number, z: number): void {
    const floorY = this.floorBelow(x, y, z); // seat the cloud on the real floor
    const ps = new ParticleSystem(`smoke-${this.smokeSeq++}`, SMOKE_CAPACITY, this.scene);
    ps.particleTexture = this.smokeSprite();
    ps.emitter = new Vector3(x, floorY + 0.3, z);
    // Puffs are born across a wide volume around the canister so the cloud reads
    // as a fat, full column rather than a thin wisp.
    ps.minEmitBox = new Vector3(-0.8, 0, -0.8);
    ps.maxEmitBox = new Vector3(0.8, 0.7, 0.8);

    // Grey, with alpha fading in then out across each particle's life — this is
    // what makes individual puffs appear and dissipate progressively. Denser
    // peak alpha + overlapping big puffs make it opaque, not see-through.
    ps.addColorGradient(0, new Color4(0.78, 0.79, 0.83, 0));
    ps.addColorGradient(0.15, new Color4(0.74, 0.75, 0.8, 0.7));
    ps.addColorGradient(0.75, new Color4(0.7, 0.71, 0.76, 0.55));
    ps.addColorGradient(1, new Color4(0.68, 0.69, 0.74, 0));
    // Each puff starts chunky and swells large as it ages → a fat cloud.
    ps.addSizeGradient(0, 1.4);
    ps.addSizeGradient(1, 5.6);

    // Heavy smoke: long-lived puffs that barely rise, so the cloud hangs around.
    ps.minLifeTime = 6;
    ps.maxLifeTime = SMOKE_MAX_LIFE;
    ps.emitRate = 95;
    ps.blendMode = ParticleSystem.BLENDMODE_STANDARD; // smoke occludes, not glows
    // Constant acceleration = a slow buoyant rise PLUS wind push (puffs ease
    // toward wind speed over a couple of seconds, so the plume leans downwind).
    ps.gravity = new Vector3(this.windX * 0.5, 0.1, this.windZ * 0.5);
    // Initial velocity: a soft outward billow with a small immediate wind bias.
    ps.direction1 = new Vector3(-0.7 + this.windX * 0.2, 0.12, -0.7 + this.windZ * 0.2);
    ps.direction2 = new Vector3(0.7 + this.windX * 0.2, 0.6, 0.7 + this.windZ * 0.2);
    ps.minEmitPower = 0.25;
    ps.maxEmitPower = 0.8;
    ps.minAngularSpeed = -0.4;
    ps.maxAngularSpeed = 0.4;
    ps.updateSpeed = 0.02;

    // Emit for SMOKE_EMIT_S, then stop; disposeOnStop tears the system down once
    // the final particles have faded — no manual bookkeeping.
    ps.targetStopDuration = SMOKE_EMIT_S;
    ps.disposeOnStop = true;
    ps.start();

    // The fuse pops the projectile out of the snapshot, so the canister mesh
    // would vanish — keep a stand-in at the source for the cloud's lifetime.
    const can = MeshBuilder.CreateCylinder('smoke-can', { height: 0.32, diameter: 0.2, tessellation: 8 }, this.scene);
    can.material = this.smokeProjMat;
    can.position.set(x, floorY + 0.16, z); // rest on the floor (half its height up)
    can.isPickable = false;
    this.fx.push({ mesh: can, kind: 'canister', bornMs: performance.now(), lifeMs: SMOKE_CANISTER_MS, r0: 1, r1: 1 });
  }

  /** Route an explosion event to the right VFX by grenade type (rockets = boom). */
  explode(x: number, y: number, z: number, kind: number, gtype: number): void {
    if (kind === ProjectileKind.Grenade && gtype === GrenadeType.Smoke) this.smoke(x, y, z);
    else if (kind === ProjectileKind.Grenade && gtype === GrenadeType.Flashbang) this.flash(x, y, z);
    else this.boom(x, y, z);
  }
}
